import React from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';

/**
 * Enhanced error state display component with specific error messages and actionable resolution steps
 * 
 * Handles various S3 error scenarios:
 * - Empty results (no files found)
 * - Connection failures (network/auth issues)
 * - File loading errors
 * - CORS configuration errors
 * - Permission errors
 */
const ErrorStateDisplay = ({ 
  error, 
  errorType = 'generic',
  onRetry,
  onOpenSettings,
  onShowCORSGuide,
  bucketName,
  activeFolder
}) => {
  // Determine error configuration based on error type
  const getErrorConfig = () => {
    // Empty results - S3 returned successfully but no files found
    if (errorType === 'empty_results' || error?.includes('No files found') || error?.includes('empty')) {
      return {
        icon: 'FolderOpen',
        iconColor: 'text-muted-foreground',
        title: 'No Files Found',
        message: activeFolder 
          ? `No files were found in the folder "${activeFolder}".`
          : `No files were found in your S3 bucket "${bucketName}".`,
        severity: 'info',
        actions: [
          {
            label: 'Upload Files',
            icon: 'Upload',
            variant: 'default',
            onClick: () => {
              // Trigger upload modal (parent component should handle this)
              const uploadButton = document.querySelector('[data-upload-trigger]');
              if (uploadButton) uploadButton?.click();
            }
          },
          {
            label: 'Retry',
            icon: 'RefreshCw',
            variant: 'outline',
            onClick: onRetry
          }
        ],
        resolutionSteps: [
          'This folder is currently empty',
          'Upload files using the "Upload Files" button',
          'Or check if you have the correct folder selected'
        ]
      };
    }

    // Connection failure - network or authentication issues
    if (errorType === 'connection_failed' || error?.includes('Failed to connect') || 
        error?.includes('Network error') ||
        error?.includes('NetworkingError')) {
      return {
        icon: 'WifiOff',
        iconColor: 'text-orange-500',
        title: 'Connection Failed',
        message: 'Unable to connect to your S3 bucket. This could be due to network issues or incorrect credentials.',
        severity: 'warning',
        actions: [
          {
            label: 'Retry Connection',
            icon: 'RefreshCw',
            variant: 'default',
            onClick: onRetry
          },
          {
            label: 'Check Settings',
            icon: 'Settings',
            variant: 'outline',
            onClick: onOpenSettings
          }
        ],
        resolutionSteps: [
          'Check your internet connection',
          'Verify AWS credentials (Access Key ID and Secret Access Key)',
          'Ensure bucket region is correct',
          'Check if bucket name exists and is spelled correctly',
          'Verify IAM permissions allow ListBucket operation'
        ]
      };
    }

    // CORS configuration error
    if (errorType === 'cors_error' || error?.includes('CORS') || 
        error?.includes('Failed to fetch')) {
      return {
        icon: 'ShieldAlert',
        iconColor: 'text-red-500',
        title: 'CORS Configuration Required',
        message: 'Your S3 bucket needs CORS (Cross-Origin Resource Sharing) configuration to allow browser access.',
        severity: 'error',
        actions: [
          {
            label: 'View CORS Guide',
            icon: 'ExternalLink',
            variant: 'default',
            onClick: onShowCORSGuide
          },
          {
            label: 'Retry After Fix',
            icon: 'RefreshCw',
            variant: 'outline',
            onClick: onRetry
          }
        ],
        resolutionSteps: [
          'Add CORS policy to your S3 bucket',
          'Allow GET, PUT, POST, DELETE methods',
          'Allow Origin: * (or your specific domain)',
          'Allow Headers: * (or specific headers)',
          'See AWS Console → S3 → Permissions → CORS configuration'
        ]
      };
    }

    // Permission denied error
    if (errorType === 'permission_denied' || error?.includes('Access denied') || 
        error?.includes('AccessDenied') ||
        error?.includes('Forbidden')) {
      return {
        icon: 'Lock',
        iconColor: 'text-red-500',
        title: 'Access Denied',
        message: 'You do not have permission to access files in this bucket.',
        severity: 'error',
        actions: [
          {
            label: 'Check Settings',
            icon: 'Settings',
            variant: 'default',
            onClick: onOpenSettings
          },
          {
            label: 'Retry',
            icon: 'RefreshCw',
            variant: 'outline',
            onClick: onRetry
          }
        ],
        resolutionSteps: [
          'Verify IAM user/role has required permissions',
          'Check bucket policy allows your AWS credentials',
          'Ensure ListBucket permission is granted',
          'Verify GetObject permission for file access',
          'Contact your AWS administrator if needed'
        ]
      };
    }

    // Bucket not found error
    if (errorType === 'bucket_not_found' || error?.includes('NoSuchBucket') ||
        error?.includes('does not exist')) {
      return {
        icon: 'AlertTriangle',
        iconColor: 'text-orange-500',
        title: 'Bucket Not Found',
        message: `The S3 bucket "${bucketName}" does not exist or cannot be accessed.`,
        severity: 'warning',
        actions: [
          {
            label: 'Update Settings',
            icon: 'Settings',
            variant: 'default',
            onClick: onOpenSettings
          },
          {
            label: 'Retry',
            icon: 'RefreshCw',
            variant: 'outline',
            onClick: onRetry
          }
        ],
        resolutionSteps: [
          'Verify bucket name is correct (case-sensitive)',
          'Check if bucket exists in your AWS account',
          'Ensure bucket is in the correct region',
          'Confirm you have access to this bucket',
          'Update bucket configuration in Settings'
        ]
      };
    }

    // File loading error (generic catch-all)
    if (errorType === 'file_load_error' || error?.includes('Failed to load')) {
      return {
        icon: 'AlertCircle',
        iconColor: 'text-red-500',
        title: 'Failed to Load Files',
        message: 'An error occurred while loading files from S3.',
        severity: 'error',
        actions: [
          {
            label: 'Retry',
            icon: 'RefreshCw',
            variant: 'default',
            onClick: onRetry
          },
          {
            label: 'Check Settings',
            icon: 'Settings',
            variant: 'outline',
            onClick: onOpenSettings
          }
        ],
        resolutionSteps: [
          'Try reloading the page',
          'Check your internet connection',
          'Verify bucket configuration in Settings',
          'Ensure AWS credentials are valid',
          'Contact support if issue persists'
        ]
      };
    }

    // Generic error (fallback)
    return {
      icon: 'AlertCircle',
      iconColor: 'text-destructive',
      title: 'Error Loading Files',
      message: error || 'An unexpected error occurred while loading files from S3.',
      severity: 'error',
      actions: [
        {
          label: 'Retry',
          icon: 'RefreshCw',
          variant: 'default',
          onClick: onRetry
        },
        {
          label: 'Settings',
          icon: 'Settings',
          variant: 'outline',
          onClick: onOpenSettings
        }
      ],
      resolutionSteps: [
        'Try refreshing the page',
        'Check your connection and credentials',
        'Review bucket configuration in Settings'
      ]
    };
  };

  const config = getErrorConfig();

  // Determine background color based on severity
  const getBgColor = () => {
    switch (config?.severity) {
      case 'info':
        return 'bg-primary/5 border-primary/20';
      case 'warning':
        return 'bg-orange-50 dark:bg-orange-950/20 border-orange-200 dark:border-orange-800';
      case 'error':
        return 'bg-destructive/5 border-destructive/20';
      default:
        return 'bg-muted/50 border-border';
    }
  };

  return (
    <div className="flex items-center justify-center py-12 px-4">
      <div className={`max-w-2xl w-full border rounded-lg p-6 ${getBgColor()}`}>
        {/* Error Icon and Title */}
        <div className="flex items-start gap-4 mb-4">
          <div className={`flex-shrink-0 ${config?.iconColor}`}>
            <Icon name={config?.icon} size={32} />
          </div>
          <div className="flex-1">
            <h3 className="text-lg font-semibold text-foreground mb-2">
              {config?.title}
            </h3>
            <p className="text-sm text-muted-foreground mb-4">
              {config?.message}
            </p>
            
            {/* Error Details (if available) */}
            {error && errorType !== 'empty_results' && (
              <div className="bg-background/50 rounded-md p-3 mb-4">
                <p className="text-xs font-mono text-muted-foreground break-all">
                  {error}
                </p>
              </div>
            )}

            {/* Resolution Steps */}
            {config?.resolutionSteps && config?.resolutionSteps?.length > 0 && (
              <div className="mb-4">
                <h4 className="text-sm font-semibold text-foreground mb-2 flex items-center gap-2">
                  <Icon name="Lightbulb" size={16} className="text-primary" />
                  How to Resolve:
                </h4>
                <ul className="space-y-1.5">
                  {config?.resolutionSteps?.map((step, index) => (
                    <li key={index} className="flex items-start gap-2 text-sm text-muted-foreground">
                      <span className="text-primary font-semibold mt-0.5">{index + 1}.</span>
                      <span>{step}</span>
                    </li>
                  ))}
                </ul>
              </div>
            )}

            {/* Action Buttons */}
            <div className="flex flex-wrap gap-3">
              {config?.actions?.map((action, index) => (
                <Button
                  key={index}
                  variant={action?.variant || 'default'}
                  iconName={action?.icon}
                  iconPosition="left"
                  onClick={action?.onClick}
                  size="sm"
                >
                  {action?.label}
                </Button>
              ))}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ErrorStateDisplay;